import type {
  DealProductBuilderRow,
  FloatingRateLoanDealProductBuilderRow,
} from '@src/types/OTC/otcProductBuilderTypes';
import { floatingRateLoanFieldDefinitions } from '@src/utils/OTC/floatingRateLoanFieldDefinitions';
import { isUnsecuredLoanSecurityType } from '@src/utils/OTC/loanSecurity';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import type { useDisclosureWithContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDealProductBuilderAssets } from '../DealProductBuilderAssetsProvider';
import { useDealProductBuilder } from '../DealProductBuilderProvider/useDealProductBuilder';
import type { DealProductBuilderColumn } from '../dealProductBuilderColDef';
import { DealProductBuilderGrid } from './DealProductBuilderGrid';
import { buildCurrencySelectorColumn } from './baseProductBuilderColumns';
import { buildLoanSecurityTypeToggleColumn } from './buildLoanSecurityTypeToggleColumn';

interface FloatingRateLoanProductBuilderGridProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

export const FloatingRateLoanProductBuilderGrid = ({
  panelID,
  productDetailsDisclosure,
}: FloatingRateLoanProductBuilderGridProps) => {
  const { availableBorrowedAssets, availableCollateralAssets } = useDealProductBuilderAssets();
  const {
    state: { rowData },
  } = useDealProductBuilder();
  const loanSecurityType = rowData[0]?.product === 'floatingRateLoan' ? rowData[0].loanSecurityType : undefined;
  const isUnsecured = isUnsecuredLoanSecurityType(loanSecurityType);

  const customColumns = useMemo(() => {
    const borrowedAssetColumn = buildCurrencySelectorColumn<FloatingRateLoanDealProductBuilderRow>({
      key: 'baseAsset',
      headerName: getColumnTitle(floatingRateLoanFieldDefinitions.borrowedAsset),
      currenciesToInclude: availableBorrowedAssets,
      description: getColumnTooltip(floatingRateLoanFieldDefinitions.borrowedAsset) ?? '',
    });
    const collateralizationColumn = buildLoanSecurityTypeToggleColumn({
      headerName: getColumnTitle(floatingRateLoanFieldDefinitions.collateralization),
      description: getColumnTooltip(floatingRateLoanFieldDefinitions.collateralization) ?? '',
    });
    // Always include Collateral Asset so AG Grid maintainColumnOrder does not append it after Loan Fields
    // when toggling Unsecured → Secured. hide keeps the header out of the DOM while unsecured.
    const collateralAssetColumn = {
      ...buildCurrencySelectorColumn<FloatingRateLoanDealProductBuilderRow>({
        key: 'collateralAsset',
        headerName: getColumnTitle(floatingRateLoanFieldDefinitions.collateralAsset),
        currenciesToInclude: availableCollateralAssets,
        description: getColumnTooltip(floatingRateLoanFieldDefinitions.collateralAsset) ?? '',
      }),
      hide: isUnsecured,
    };
    return [borrowedAssetColumn, collateralizationColumn, collateralAssetColumn] as DealProductBuilderColumn[];
  }, [availableBorrowedAssets, availableCollateralAssets, isUnsecured]);

  return (
    <DealProductBuilderGrid
      panelID={panelID}
      productDetailsDisclosure={productDetailsDisclosure}
      customColumnDefs={customColumns}
      fieldDefinitions={floatingRateLoanFieldDefinitions}
    />
  );
};
